<template lang="html">
  <sc-flex :is-column="true" :is-full-width="true">
    <PaymentCompletedDialog
      :opened.sync="paymentCompletedDialogOpened"
      :taxReturn="taxReturn"
    />

    <PaymentVoucherDialog
      v-if="currentVoucher"
      :opened.sync="paymentVoucherDialogOpened"
      :company-id="companyId"
      :current-voucher="currentVoucher"
      :tax-return="taxReturn"
      :voucher-definitions="voucherDefinitions"
    />

    <!-- Partial: Alert -->
    <TaxReturnAlert>
      <sc-alert type="info">
        <sc-small :line-clamp="2">
          <b>Instruction:</b> Please click the <b>Make Payment</b> button to
          make payments. Once you've made payments, please click the
          <b>Mark as Paid</b> checkbox.
        </sc-small>
      </sc-alert>
    </TaxReturnAlert>

    <!-- Partial: Content -->
    <v-layout class="full-height-container">
      <div class="vouchers-container">
        <v-expansion-panel :expand="true" v-model="expanded">
          <!-- Partial: Invoice payment -->
          <v-expansion-panel-content
            v-if="!excludeInvoicePayment && taxReturn.payment"
            :key="invoice"
          >
            <template v-slot:header>
              <v-layout class="voucher-header justify-space-between">
                <h2><span>Due now</span></h2>

                <v-chip
                  v-if="taxReturn.payment.status === 'paid'"
                  color="success"
                  small
                  class="white--text"
                >
                  Completed
                </v-chip>
              </v-layout>
            </template>

            <v-layout class="voucher-content" column>
              <v-layout justify-space-between>
                <v-layout>
                  <h3 class="text-xs-left payment-name">
                    <span>
                      {{ currentFirm.name }}'s Tax Service
                      <br />
                      <span class="grey--text text--darken-1 body-2">
                        <span v-if="taxReturn.payment.status === 'paid'">
                          Payment was processed successfully!
                        </span>
                        <span
                          v-else-if="taxReturn.payment.status === 'pending'"
                        >
                          Your payment is awaiting processing, which might take
                          a few days. Please check back later.
                        </span>
                        <span v-else>
                          Please make payment to download your tax return.
                        </span>
                      </span>
                    </span>

                    <v-chip
                      v-if="taxReturn.payment.status === 'paid'"
                      color="success"
                      small
                      class="white--text"
                    >
                      Paid
                    </v-chip>
                  </h3>
                </v-layout>

                <div class="voucher-payment">
                  <v-layout align-center>
                    <h3 class="mr-2 font-weight-regular">
                      <span class="body-1 grey--text">Amount to Pay</span><br />
                      <span>{{
                        formatCurrency(taxReturn.payment.amount / 100)
                      }}</span>
                    </h3>
                  </v-layout>
                </div>
              </v-layout>

              <v-layout
                class="mt-2 payment-instruction"
                align-center
                justify-center
              >
                <v-btn
                  v-if="
                    taxReturn.payment.status !== 'paid' &&
                      taxReturn.payment.status !== 'pending'
                  "
                  class="text-none px-3"
                  color="primary"
                  @click="createCheckoutSession"
                  :loading="creatingCheckoutSession"
                >
                  <v-icon class="mr-1">add_card</v-icon>
                  Make Payment
                </v-btn>

                <v-btn
                  class="text-none px-3"
                  color="success"
                  @click="downloadTaxReturn(false)"
                  :disabled="taxReturn.payment.status !== 'paid'"
                  :loading="downloadingTaxReturn"
                >
                  <v-icon class="mr-1">download</v-icon>
                  Download tax return
                </v-btn>
                <v-btn
                  class="text-none px-3"
                  color="success"
                  @click="downloadTaxReturn(true)"
                  :disabled="taxReturn.payment.status !== 'paid'"
                  :loading="downloadingTaxReturn"
                >
                  <v-icon class="mr-1">download</v-icon>
                  Download tax return with other pages
                </v-btn>
              </v-layout>
            </v-layout>
          </v-expansion-panel-content>

          <v-expansion-panel-content
            v-if="instructionVouchers.length > 0"
            key="instruction-pages"
          >
            <template v-slot:header>
              <v-layout class="voucher-header justify-space-between">
                <h2><span>Instruction for Payment Vouchers</span></h2>
                <v-chip color="info" small class="white--text">
                  {{ instructionVouchers.length }} page{{
                    instructionVouchers.length > 1 ? 's' : ''
                  }}
                </v-chip>
              </v-layout>
            </template>
            <v-layout class="voucher-content" column>
              <v-layout
                v-for="(voucherPair, pairIndex) in instructionVoucherPairs"
                :key="pairIndex"
                class="instruction-pair"
                justify-space-between
              >
                <v-layout
                  v-for="(voucher, index) in voucherPair"
                  :key="index"
                  class="instruction-content"
                  column
                  style="flex: 1; margin: 0 8px;"
                >
                  <v-layout class="mt-2" justify-center>
                    <div
                      v-if="
                        voucher.page && instructionPageImages[voucher.page - 1]
                      "
                      class="instruction-pdf-container"
                    >
                      <img
                        :src="instructionPageImages[voucher.page - 1].thumbnail"
                        class="instruction-pdf"
                        alt="Instruction page"
                      />
                    </div>
                    <div
                      v-else-if="voucher.page"
                      class="instruction-pdf-container"
                    >
                      <v-progress-circular indeterminate color="primary" />
                    </div>
                    <div v-else class="instruction-page-info">
                      <v-alert type="warning" :value="true" outline>
                        <span
                          >No document available for this instruction
                          page.</span
                        >
                      </v-alert>
                    </div>
                  </v-layout>
                </v-layout>
              </v-layout>
            </v-layout>
          </v-expansion-panel-content>

          <!-- Partial: Active tax payments -->
          <v-expansion-panel-content
            v-for="(vouchers, due_date) of activeVouchersByDueDate"
            :key="due_date ? due_date : 'no-due-date'"
          >
            <template v-slot:header>
              <v-layout class="voucher-header justify-space-between">
                <h2>
                  <span v-if="!isEmpty(due_date) && due_date !== 'undefined'"
                    >Due date: {{ due_date }}</span
                  >
                  <span v-else>No due date specified</span>
                </h2>

                <v-chip
                  v-if="
                    vouchers
                      .filter(voucher => !voucher.is_archived)
                      .filter(voucher => !voucher.is_paid).length === 0
                  "
                  color="success"
                  small
                  class="white--text"
                >
                  Completed
                </v-chip>
              </v-layout>
            </template>

            <v-layout
              v-for="(voucher, index) in vouchers"
              :key="index"
              class="voucher-content"
              column
            >
              <v-layout justify-space-between>
                <v-layout align-center>
                  <h3 class="text-xs-left payment-name">
                    <span>
                      <span>
                        {{ getFullPaymentName(voucher) }}
                      </span>

                      <br />

                      <span
                        v-if="
                          voucher.voucher_id !== 'other' &&
                            getVoucherDefinitionById(voucher.voucher_id)
                        "
                        class="grey--text body-2"
                      >
                        {{
                          getVoucherDefinitionById(voucher.voucher_id).code
                        }}</span
                      >
                    </span>

                    <v-chip
                      v-if="voucher.is_paid"
                      color="success"
                      small
                      class="white--text"
                    >
                      Paid
                    </v-chip>
                  </h3>
                </v-layout>

                <div class="voucher-payment">
                  <v-layout align-center>
                    <h3 class="mr-2 font-weight-regular">
                      <span class="body-1 grey--text">Amount to Pay</span><br />
                      <span>{{ formatCurrency(voucher.amount) }}</span>
                    </h3>
                  </v-layout>
                </div>
              </v-layout>

              <v-layout
                class="mt-2 payment-instruction"
                align-center
                justify-center
              >
                <v-btn
                  class="text-none px-3"
                  color="primary"
                  @click="showPaymentDialog(voucher)"
                  :disabled="voucher.is_paid"
                >
                  <v-icon class="mr-1">add_card</v-icon>
                  Make Payment
                </v-btn>

                <v-btn
                  class="text-none px-3"
                  color="primary"
                  outline
                  @click="downloadVoucher(voucher)"
                  v-if="voucher.document_location || voucher.page"
                >
                  <v-icon class="mr-1">download</v-icon>
                  Download
                </v-btn>

                <v-checkbox
                  hide-details
                  v-model="voucher.is_paid"
                  class="mt-0 ml-1 px-2 mark-as-paid-checkbox"
                  @change="updateVouchers"
                >
                  <template slot="label">
                    <span class="ml-0" style="width: 100px">Mark as Paid</span>
                  </template>
                </v-checkbox>
              </v-layout>

              <v-divider v-if="index !== vouchers.length - 1" class="mt-2" />
            </v-layout>
          </v-expansion-panel-content>

          <!-- Partial: Archived tax payments -->
          <v-expansion-panel-content
            v-if="archivedVouchers.length > 0"
            key="archived-vouchers"
          >
            <template v-slot:header>
              <v-layout class="voucher-header justify-space-between">
                <h2><span>Archived vouchers</span></h2>

                <v-chip color="error" small class="white--text">
                  Do not pay
                </v-chip>
              </v-layout>
            </template>

            <v-layout
              v-for="(voucher, index) in archivedVouchers"
              :key="index"
              class="voucher-content"
              column
            >
              <v-layout justify-space-between>
                <v-layout align-center>
                  <h3 class="text-xs-left payment-name">
                    <span>
                      <span>
                        {{ getFullPaymentName(voucher) }}
                      </span>

                      <br />

                      <span
                        v-if="
                          voucher.voucher_id !== 'other' &&
                            getVoucherDefinitionById(voucher.voucher_id)
                        "
                        class="grey--text body-2"
                      >
                        {{
                          getVoucherDefinitionById(voucher.voucher_id).code
                        }}</span
                      >
                    </span>

                    <v-chip
                      v-if="voucher.is_paid"
                      color="success"
                      small
                      class="white--text"
                    >
                      Paid
                    </v-chip>
                  </h3>
                </v-layout>

                <div class="voucher-payment">
                  <v-layout align-center>
                    <h3 class="mr-2 font-weight-regular">
                      <span class="body-1 grey--text">Amount</span><br />
                      <span>{{ formatCurrency(voucher.amount) }}</span>
                    </h3>
                  </v-layout>
                </div>
              </v-layout>

              <v-divider
                v-if="index !== archivedVouchers.length - 1"
                class="mt-2"
              />
            </v-layout>
          </v-expansion-panel-content>
        </v-expansion-panel>

        <v-spacer style="height: 40px" />
      </div>
    </v-layout>
  </sc-flex>
</template>

<script>
import groupBy from 'lodash/groupBy';
import axios from 'axios';
import isEmpty from 'lodash/isEmpty';
import PaymentVoucherDialog from '@/pages/tax-return-delivery/components/PaymentVoucherDialog';
import toPairs from 'lodash/toPairs';
import API from '@/api';
import { mapGetters } from 'vuex';
import UtilsMixins from '@/mixins/UtilsMixins';
import PaymentCompletedDialog from '@/pages/tax-return-delivery/components/PaymentCompletedDialog';
import TaxReturnAlert from './components/TaxReturnAlert';
import PreviewDocument from '@/components/PreviewDocument';
import { PDFDocument } from 'pdf-lib';
import { generatePdfThumbnails } from '@/utils/pdf';
import download from 'downloadjs';

export default {
  mixins: [UtilsMixins],

  components: {
    PaymentVoucherDialog,
    PaymentCompletedDialog,
    TaxReturnAlert,
    PreviewDocument,
  },

  props: {
    companyId: {
      type: String,
      default: null,
    },

    taxReturn: {
      type: Object,
      required: true,
    },

    excludeInvoicePayment: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      // For stripe payment
      creatingCheckoutSession: false,

      voucherDefinitions: [],

      // For payment voucher dialog
      paymentVoucherDialogOpened: false,
      currentVoucher: null,

      downloadingTaxReturn: false,

      expanded: [],
      paymentCompletedDialogOpened: false,

      // For instruction page PDFs
      instructionPageImages: {},
    };
  },

  methods: {
    isEmpty,

    formatCurrency(amount) {
      if (amount === 0 || amount === null) {
        return '0.00';
      }

      const formatter = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
      });

      return formatter.format(amount);
    },

    showPaymentDialog(voucher) {
      this.currentVoucher = voucher;
      this.paymentVoucherDialogOpened = true;
    },

    getVoucherDefinitionById(id) {
      return this.voucherDefinitions.find(voucher => voucher.id === id);
    },

    async getVoucherDefinitions() {
      const response = await axios.get(`/tax_returns/get_voucher_definitions`);
      this.voucherDefinitions = response.data;
    },

    async updateVouchers() {
      try {
        const response = await axios.patch(
          `/tax_returns/${this.taxReturn.id}`,
          {
            company_id: this.companyId,
            payment_vouchers: this.taxReturn.payment_vouchers,
          },
        );

        this.$emit('tax-return-updated', response.data);
      } catch (error) {
        console.log(error);
        this.$store.dispatch('application/setErrorNotification', {
          message: 'There was a problem signing the document.',
          errorToSend: error,
          userEmail: this.currentUser.email,
        });
      }
    },

    expandUnpaidVouchers() {
      // Always expand the invoice section for download
      if (!this.excludeInvoicePayment && this.taxReturn.payment) {
        this.expanded = ['invoice'];
      }

      // Always expand instruction pages section if there are any
      if (this.instructionVouchers.length > 0) {
        this.expanded.push('instruction-pages');
      }

      this.expanded = this.expanded.concat(
        toPairs(this.activeVouchersByDueDate).map(([dueDate, vouchers]) => {
          return vouchers.filter(voucher => !voucher.is_paid).length > 0;
        }),
      );
    },

    async createCheckoutSession() {
      try {
        this.creatingCheckoutSession = true;
        const response = await API.taxReturn.createCheckoutSession({
          id: this.taxReturn.id,
          companyId: this.taxReturn.company_id,
        });

        window.location = response.data.checkout_url;
      } catch (error) {
        console.log(error);
        this.$store.dispatch('application/setErrorNotification', {
          message: 'There was a problem creating the checkout session.',
          errorToSend: error,
          userEmail: this.currentUser.email,
        });
      } finally {
        this.creatingCheckoutSession = false;
      }
    },

    async downloadTaxReturn(includeOtherPages = false) {
      try {
        this.downloadingTaxReturn = true;
        const response = await API.taxReturn.downloadTaxReturn({
          id: this.taxReturn.id,
          companyId: this.taxReturn.company_id,
          otherPagesIncluded: includeOtherPages,
        });

        this.downloadDocumentByLink(response.data.link);
      } catch (error) {
        this.$store.dispatch('application/setErrorNotification', {
          message: 'There was a problem downloading tax return.',
          errorToSend: error,
          userEmail: this.currentUser.email,
        });
      } finally {
        this.downloadingTaxReturn = false;
      }
    },

    getFullPaymentName(paymentVoucher) {
      let paymentName =
        paymentVoucher.voucher_id === 'other'
          ? !isEmpty(paymentVoucher.custom_payment_name)
            ? paymentVoucher.custom_payment_name
            : 'Other'
          : this.getVoucherDefinitionById(paymentVoucher.voucher_id)
          ? `${
              this.getVoucherDefinitionById(paymentVoucher.voucher_id).state
            } ${this.getVoucherDefinitionById(paymentVoucher.voucher_id).name}`
          : 'Unknown';

      if (paymentVoucher.quarter && paymentVoucher.quarter !== 'None') {
        paymentName += ` (${paymentVoucher.quarter})`;
      }

      return paymentName;
    },

    async loadInstructionPageThumbnails() {
      try {
        const response = await API.integration.downloadDocument({
          companyId: this.companyId,
          link: this.taxReturn.watermark_enabled
            ? this.taxReturn.watermarked_file
            : this.taxReturn.original_file,
        });

        const pdfSrc = response.data.document.data.link;
        const pdfBytes = await fetch(pdfSrc).then(res => res.arrayBuffer());
        const pdfDoc = await PDFDocument.load(pdfBytes);
        const pdfData = await pdfDoc.saveAsBase64({ dataUri: true });
        const pageImages = await generatePdfThumbnails(pdfData, 600);

        this.instructionPageImages = pageImages;
      } catch (error) {
        console.error('Error loading instruction page thumbnail:', error);
      }
    },

    async downloadVoucher(voucher) {
      const voucherDefinition = this.getVoucherDefinitionById(
        voucher.voucher_id,
      );

      if (voucher.document_location) {
        const response = await API.integration.downloadDocument({
          companyId: this.companyId,
          link: voucher.document_location,
        });

        await this.downloadDocumentByLink(
          response.data.document.data.link,
          voucherDefinition.id + '.pdf',
        );
      } else {
        const response = await API.integration.downloadDocument({
          companyId: this.companyId,
          link: this.taxReturn.watermark_enabled
            ? this.taxReturn.watermarked_file
            : this.taxReturn.original_file,
        });

        const pdfSrc = response.data.document.data.link;
        const pdfBytes = await fetch(pdfSrc).then(res => res.arrayBuffer());

        const pdfDoc = await PDFDocument.load(pdfBytes);

        const voucherPdf = await PDFDocument.create();
        const [voucherPage] = await voucherPdf.copyPages(pdfDoc, [
          voucher.page - 1,
        ]);
        voucherPdf.addPage(voucherPage);

        const voucherData = await voucherPdf.saveAsBase64({ dataUri: true });

        return download(
          voucherData,
          voucherDefinition.id + '.pdf',
          'application/pdf',
        );
      }
    },
  },

  computed: {
    ...mapGetters({
      currentFirm: 'currentFirm',
      currentUser: 'currentUser',
    }),

    activeVouchers() {
      return this.taxReturn.payment_vouchers
        .filter(voucher => {
          return !voucher.is_archived;
        })
        .filter(voucher => {
          return !voucher.is_instruction_page;
        });
    },

    instructionVouchers() {
      return this.taxReturn.payment_vouchers.filter(voucher => {
        return !voucher.is_archived && voucher.is_instruction_page;
      });
    },

    instructionVoucherPairs() {
      const pairs = [];
      for (let i = 0; i < this.instructionVouchers.length; i += 2) {
        const pair = this.instructionVouchers.slice(i, i + 2);
        pairs.push(pair);
      }
      return pairs;
    },

    archivedVouchers() {
      return this.taxReturn.payment_vouchers.filter(voucher => {
        return voucher.is_archived;
      });
    },

    allPaid() {
      const allVoucherPaids = this.activeVouchers
        .filter(voucher => !voucher.is_archived)
        .every(voucher => voucher.is_paid);

      // If there is an invoice payment, check if it's paid as well.
      if (!this.excludeInvoicePayment && this.taxReturn.payment) {
        return allVoucherPaids && this.taxReturn.payment.status === 'paid';
      } else {
        return allVoucherPaids;
      }
    },

    activeVouchersByDueDate() {
      return groupBy(
        this.activeVouchers.sort((a, b) => {
          if (a.is_due_asap) return -1;
          else if (b.is_due_asap) return 1;
          else if (isEmpty(a.due_date)) return -1;
          else if (isEmpty(b.due_date)) return 1;
          else return new Date(a.due_date) - new Date(b.due_date);
        }),
        voucher =>
          voucher.is_due_asap ? 'As soon as possible' : voucher.due_date,
      );
    },
  },

  watch: {
    allPaid() {
      if (this.allPaid) {
        this.paymentCompletedDialogOpened = true;
      }
    },
  },

  async created() {
    await this.getVoucherDefinitions();
    this.expandUnpaidVouchers();
    this.loadInstructionPageThumbnails();
  },
};
</script>

<style lang="postcss" scoped>
.sc-alert {
  position: sticky;
  top: 0;

  margin: 0px;
  padding: 8px;

  background-color: var(--blue-600);
  color: var(--gray-0);
}

.full-height-container {
  overflow: auto;
  min-height: calc(100vh - 72px - 58px);
  max-height: calc(100vh - 72px - 58px);
  background-color: #f0f0f0;
  max-width: 100%;
}

.vouchers-container {
  flex: auto;
  min-width: 0;
  max-width: 1000px;
  margin: 0 auto;
  padding: 16px;
}

.v-expansion-panel {
  box-shadow: none;
}

.v-expansion-panel__container {
  border: 1px solid #ccc;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
  background-color: #fff;
  margin: 12px 0;
  border-radius: 8px !important;
  overflow: hidden;
}

.v-expansion-panel__container {
  & >>> .v-expansion-panel__header {
    background-color: #f7f7f7;
    border-bottom: 1px solid #ccc;
  }
}

.voucher-header {
  padding: 8px 0px;
  font-size: 14px;
}

.voucher-content {
  padding: 12px 14px;
}

.voucher-payment {
  display: flex;
  flex-direction: column;
  align-items: end;
}

.payment-name {
  font-size: 17px;
  font-weight: normal;
}

.mark-as-paid-checkbox {
  flex: 0;
  padding: 5px;
  border-radius: 4px;

  & >>> .v-input--selection-controls__input .theme--light.v-icon {
    color: #888;
  }

  & >>> .v-input--selection-controls.v-input .v-label {
    display: block;
  }

  & >>> .v-input--selection-controls.v-input .v-label span {
    font-size: 14px;
  }
}

.payment-instruction {
  display: flex;
  flex-direction: row !important;
}

.instruction-content {
  padding: 8px 0;
}

.instruction-pair {
  margin-bottom: 16px;
}

.instruction-pdf-container {
  width: 100%;
  max-width: 400px;
  margin: 0 auto;
}

.instruction-pdf {
  width: 100%;
  max-width: 100%;
  border: 1px solid var(--gray-300);
  border-radius: 4px;
}

.instruction-page-info {
  width: 100%;
  max-width: 600px;
  margin: 0 auto;
}

@media (max-width: 640px) {
  .payment-instruction {
    gap: 8px;
    flex-direction: column !important;
  }

  .instruction-pdf-container {
    max-width: 100%;
  }

  .instruction-pair {
    flex-direction: column !important;
  }

  .instruction-content {
    margin: 8px 0 !important;
  }
}
</style>
